import { differenceInMinutes, format } from 'date-fns';

import { ProductType } from '@/components/products/ProductsPromptModal/redux/types';
import { formatPrice, getCurrencySymbol } from '@/components/products/utils';
import { GetProductsResponse, ParsedUserProduct, Product, ProductSourceName } from '@/queries/products/types';

export const DAY_PASS_LOOKUP_KEYS = ['single-habitat-day-pass', 'all-habitats-day-pass'] as const;
export const LIFETIME_EXPLORER_KEY = 'lifetime-explorer';
export const SPONSORSHIP_TIER_ONE_LOOKUP_KEYS = 'monthly-supporter-tier-1,annual-supporter-tier-1';
export const EXPLORER_PAIR_LOOKUP_KEYS = 'monthly-member,annual-member';
export const EDUCATOR_PAIR_LOOKUP_KEYS = 'monthly-educator,annual-educator';

export const DETAILS_SECTION_CLASS =
  'preflight divide-y divide-stone-200 desktop:px-6 [&:not(:last-child)]:mobile:border-b [&:not(:last-child)]:mobile:border-stone-200 [&:not(:last-child)]:desktop:shadow-[2px_2px_7px_0px_rgba(0,0,0,0.25)]';

export type UserProductDetailView = 'recurring' | 'sponsorship' | 'fallback' | 'unsupported';
export type UpsellType = 'day-pass' | 'explorer' | 'sponsorship';

export const isDayPassLookupKey = (productLookupKey: string | undefined) =>
  DAY_PASS_LOOKUP_KEYS.includes(productLookupKey as (typeof DAY_PASS_LOOKUP_KEYS)[number]);

export const isStripeManagedUserProduct = (userProduct: ParsedUserProduct) =>
  userProduct.product?.source === 'stripe' &&
  (userProduct.productKind === 'recurring' || userProduct.productKind === 'sponsorship');

export const isFallbackDetailProduct = (userProduct: ParsedUserProduct) => {
  const { source, productLookupKey } = userProduct.product;
  return (
    source === 'bundling' ||
    source === 'giftup' ||
    productLookupKey === LIFETIME_EXPLORER_KEY ||
    (source === 'stripe' && userProduct.productKind === 'single-use' && isDayPassLookupKey(productLookupKey))
  );
};

export const isUserProductDetailsSupported = (userProduct: ParsedUserProduct) =>
  isStripeManagedUserProduct(userProduct) || isFallbackDetailProduct(userProduct);

export const getUserProductDetailView = (userProduct: ParsedUserProduct): UserProductDetailView => {
  if (userProduct.productKind === 'recurring' && userProduct.product.source === 'stripe') {
    return 'recurring';
  }
  if (userProduct.productKind === 'sponsorship' && userProduct.product.source === 'stripe') {
    return 'sponsorship';
  }
  if (isFallbackDetailProduct(userProduct)) {
    return 'fallback';
  }
  return 'unsupported';
};

export const getBundlingZooId = (metadata: ParsedUserProduct['product']['metadata'] | undefined) =>
  metadata?.zooId ?? metadata?.zooID;

export const getUserProductImageVariant = (source: ProductSourceName | undefined): 'default' | 'giftup' | 'bundling' => {
  if (source === 'giftup') return 'giftup';
  if (source === 'bundling') return 'bundling';
  return 'default';
};

export const selectProductsMap = (data: GetProductsResponse) =>
  data.list.reduce((acc, product) => {
    if (!product.productLookupKey) return acc;
    acc.set(product.productLookupKey, product);
    return acc;
  }, new Map<string, Product>());

export const getRecurringPairLookupKey = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-member':
    case 'annual-member':
      return EXPLORER_PAIR_LOOKUP_KEYS;
    case 'monthly-educator':
    case 'annual-educator':
      return EDUCATOR_PAIR_LOOKUP_KEYS;
    default:
      return undefined;
  }
};

export const getSponsorshipTierLookupKey = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'monthly-supporter-tier-1,annual-supporter-tier-1';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'monthly-supporter-tier-2,annual-supporter-tier-2';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'monthly-supporter-tier-3,annual-supporter-tier-3';
    default:
      return undefined;
  }
};

export const getSwitchableProduct = (
  productLookupKey: string | undefined,
  productsMap: Map<string, Product> | undefined,
) => {
  if (!productsMap) return undefined;
  switch (productLookupKey) {
    case 'monthly-member':
      return productsMap.get('annual-member');
    case 'annual-member':
      return productsMap.get('monthly-member');
    case 'monthly-educator':
      return productsMap.get('annual-educator');
    case 'annual-educator':
      return productsMap.get('monthly-educator');
    case 'monthly-supporter-tier-1':
      return productsMap.get('annual-supporter-tier-1');
    case 'annual-supporter-tier-1':
      return productsMap.get('monthly-supporter-tier-1');
    case 'monthly-supporter-tier-2':
      return productsMap.get('annual-supporter-tier-2');
    case 'annual-supporter-tier-2':
      return productsMap.get('monthly-supporter-tier-2');
    case 'monthly-supporter-tier-3':
      return productsMap.get('annual-supporter-tier-3');
    case 'annual-supporter-tier-3':
      return productsMap.get('monthly-supporter-tier-3');
    default:
      return undefined;
  }
};

export const getPromotedProductType = (productLookupKey: string | undefined): ProductType | undefined => {
  switch (productLookupKey) {
    case 'monthly-member':
    case 'annual-member':
      return 'educator-pass';
    case 'monthly-educator':
    case 'annual-educator':
      return 'explorer-pass';
    default:
      return undefined;
  }
};

export const getProductTitle = (productKey: string | undefined, productName: string | undefined, habitatName?: string) => {
  switch (productKey) {
    case 'single-habitat-day-pass':
      return `Day Pass: ${habitatName || 'Unknown Habitat'}`;
    case 'all-habitats-day-pass':
      return 'All-Access Day Pass';
    case 'monthly-supporter-tier-1':
    case 'monthly-supporter-tier-2':
    case 'monthly-supporter-tier-3':
      return `Monthly ${habitatName || 'Unknown Habitat'} Supporter`;
    case 'annual-supporter-tier-1':
    case 'annual-supporter-tier-2':
    case 'annual-supporter-tier-3':
      return `Yearly ${habitatName || 'Unknown Habitat'} Supporter`;
    case 'monthly-member':
      return 'Monthly Explorer Plan';
    case 'annual-member':
      return 'Yearly Explorer Plan';
    case 'monthly-educator':
      return 'Monthly Educator Plan';
    case 'annual-educator':
      return 'Yearly Educator Plan';
    default:
      return productName || productKey || 'Unknown Product';
  }
};

export const toProductType = (productLookupKey: string | undefined): ProductType | undefined => {
  switch (productLookupKey) {
    case 'single-habitat-day-pass':
      return 'single-habitat-day-pass';
    case 'all-habitats-day-pass':
      return 'all-habitats-day-pass';

    case 'monthly-supporter-tier-1':
    case 'monthly-supporter-tier-2':
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-1':
    case 'annual-supporter-tier-2':
    case 'annual-supporter-tier-3':
      return 'sponsorship';

    case 'monthly-member':
    case 'annual-member':
      return 'explorer-pass';

    case 'monthly-educator':
    case 'annual-educator':
      return 'educator-pass';
    default:
      return undefined;
  }
};

export const getProductDescription = (userProduct: ParsedUserProduct, now: number) => {
  const { product, validUntil } = userProduct;
  const productLookupKey = product?.productLookupKey;

  if (validUntil < now) {
    return `Expired ${format(validUntil, isDayPassLookupKey(productLookupKey) ? 'h:mm a MMM d yyyy' : 'MMMM d yyyy')}`;
  }
  if (isDayPassLookupKey(productLookupKey)) {
    const totalMinutes = differenceInMinutes(validUntil, now);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `Expiring in ${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
  }
  if (productLookupKey === LIFETIME_EXPLORER_KEY) {
    return `Lifetime Access`;
  }
  if (!isStripeManagedUserProduct(userProduct)) {
    return `Expires ${format(validUntil, 'MMMM d yyyy')}`;
  }
  if (!userProduct.scheduledProductId) {
    return `Scheduled cancel ${format(validUntil, 'MMMM d')}`;
  }
  if (userProduct.scheduledProductId !== userProduct.productId) {
    return `Scheduled change ${format(validUntil, 'MMMM d')}`;
  }
  return (
    <>
      Renews {format(validUntil, 'MMMM d')}
      <span className="mobile:hidden"> {format(validUntil, 'yyyy')}</span>
    </>
  );
};

export const getProductPrice = (priceObject: Pick<Product['price'], 'currency' | 'price' | 'interval'> | undefined) => {
  if (!priceObject) return undefined;
  const price = `${getCurrencySymbol(priceObject.currency)} ${formatPrice(priceObject.price ?? 0)}`;
  if (priceObject.interval === 'month') {
    return `${price}/Mo`;
  }
  if (priceObject.interval === 'year') {
    return `${price}/Yr`;
  }
  if (priceObject.interval === 'week') {
    return `${price}/Wk`;
  }
  return price;
};

export const getProductMonthlyPrice = (
  priceObject: Pick<Product['price'], 'currency' | 'price' | 'interval'> | undefined,
) => {
  if (!priceObject) return undefined;
  const price = priceObject.interval === 'month' ? priceObject.price : Math.floor(priceObject.price / 12);
  return `${getCurrencySymbol(priceObject.currency)}${formatPrice(price)}/Mo`;
};

export const hasActiveSponsorshipForHabitat = (activeUserProducts: ParsedUserProduct[], habitatId: string) =>
  activeUserProducts.some(
    (product) => product.productKind === 'sponsorship' && product.associatedHabitatIds?.includes(habitatId),
  );

export const resolveUpsellProduct = ({
  userProduct,
  activeUserProducts,
  hasGuidedExperience,
  hasSingletonFeature,
  hasExplorerFeature,
}: {
  userProduct: ParsedUserProduct;
  activeUserProducts: ParsedUserProduct[];
  hasGuidedExperience: boolean;
  hasSingletonFeature: boolean;
  hasExplorerFeature: boolean;
}): UpsellType | null => {
  const detailView = getUserProductDetailView(userProduct);

  if (detailView === 'sponsorship') {
    return hasExplorerFeature ? null : 'explorer';
  }

  if (detailView !== 'fallback') {
    return null;
  }

  const { source, productLookupKey } = userProduct.product;
  const habitatId = userProduct.associatedHabitatIds?.[0];

  if (productLookupKey === 'single-habitat-day-pass' && habitatId) {
    return hasActiveSponsorshipForHabitat(activeUserProducts, habitatId) ? null : 'sponsorship';
  }

  if (productLookupKey === 'all-habitats-day-pass') {
    return hasSingletonFeature ? null : 'explorer';
  }

  if (source === 'bundling' && hasGuidedExperience) {
    return null;
  }

  if (!hasGuidedExperience) {
    return 'day-pass';
  }

  if (!hasSingletonFeature) {
    return 'explorer';
  }

  return null;
};
